import type { MessageBlock, SuggestedFollowUp } from "@/lib/chat-types";
import { toRecord, type JsonRecord } from "@/lib/fund-compliance/parse-primitives";
import { extractCitationBlocks } from "@/lib/fund-compliance/parse-citations";
import { extractReviewBlocks } from "@/lib/fund-compliance/parse-findings";
import { buildHistoryReportData } from "@/lib/fund-compliance/parse-progress";

/* ── Suggested follow-ups ────────────────────────────────────────── */

export function extractSuggestedFollowUps(value: unknown): SuggestedFollowUp[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((item) => {
      const record = toRecord(item);
      const text = record
        ? (typeof record.text === "string" ? record.text : undefined)
        : typeof item === "string"
          ? item
          : undefined;
      if (!text) return null;
      return { text } satisfies SuggestedFollowUp;
    })
    .filter((item): item is SuggestedFollowUp => item !== null);
}

/* ── Public API ──────────────────────────────────────────────────── */

export interface MetadataExtraction {
  blocks: MessageBlock[];
  suggestedFollowUps: SuggestedFollowUp[];
  /**
   * Raw review payload (e.g. metadata.structured.review). Exposed so callers
   * can detect which fields are being rendered as structured blocks and
   * strip the matching sections from the narrative text to avoid
   * duplicate rendering. Null when the message carries no review data.
   */
  reviewRecord: JsonRecord | null;
}

/**
 * Extract structured blocks from message metadata.
 * Looks for review data, citations, and follow-up suggestions.
 *
 * The new schema nests structured fields under `metadata.structured.*`
 * (e.g. `metadata.structured.followUps`). Older responses may place them
 * directly under `metadata.*`. We check both so both shapes keep working.
 *
 * Note: `retrievedSources` is intentionally ignored — attachment references
 * are sourced from `citations` exclusively.
 */
export function extractMetadataBlocks(value: unknown): MetadataExtraction {
  const record = toRecord(value);
  if (!record) {
    return { blocks: [], suggestedFollowUps: [], reviewRecord: null };
  }

  const structured = toRecord(record.structured);

  // Helper: prefer structured.<key> if present, otherwise fall back to record.<key>
  const pick = (key: string): unknown => structured?.[key] ?? record[key];

  const answerRecord = toRecord(pick("answer"));
  const reviewRecord = toRecord(pick("review"));
  const reviewBlocks = extractReviewBlocks(reviewRecord);
  const historyReportData = buildHistoryReportData(reviewRecord?.report);
  const citationBlocks = extractCitationBlocks(answerRecord?.citations ?? pick("citations"));

  const reportBlocks: MessageBlock[] = historyReportData
    ? [{ type: "report-progress", reportProgress: historyReportData }]
    : [];

  return {
    blocks: [...reviewBlocks, ...reportBlocks, ...citationBlocks],
    suggestedFollowUps: extractSuggestedFollowUps(pick("followUps")),
    reviewRecord,
  };
}
